import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { CcProject, CcTask } from "@shared/schema";
import { Folder, CheckSquare, AlertCircle, TrendingUp, Plus, Edit, Circle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function ProjectManagerDashboard() {
  const { data: projects, isLoading: projectsLoading } = useQuery<CcProject[]>({
    queryKey: ["/api/control-centre/projects"],
  });

  const { data: tasks, isLoading: tasksLoading } = useQuery<CcTask[]>({
    queryKey: ["/api/control-centre/tasks"],
  });

  const isLoading = projectsLoading || tasksLoading;

  // Calculate stats
  const stats = {
    activeProjects: projects?.filter(p => p.status === "active").length || 0,
    totalTasks: tasks?.length || 0,
    completedTasks: tasks?.filter(t => t.status === "completed").length || 0,
    blockedTasks: tasks?.filter(t => t.status === "blocked").length || 0,
  };

  const completionRate = stats.totalTasks > 0 
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100) 
    : 0;

  // Generate recent activity feed
  type ActivityItem = {
    id: string;
    type: "project" | "task";
    action: "created" | "updated";
    title: string;
    status?: string;
    priority?: string;
    timestamp: Date;
    projectName?: string;
  };

  const recentActivity: ActivityItem[] = [];

  if (projects && tasks) {
    // Add project activities
    projects.forEach(project => {
      const createdDate = new Date(project.createdAt);
      const updatedDate = new Date(project.updatedAt);
      
      recentActivity.push({
        id: `project-created-${project.id}`,
        type: "project",
        action: "created",
        title: project.projectName,
        status: project.status,
        priority: project.priority,
        timestamp: createdDate,
      });

      // Only add update activity if it's different from creation
      if (updatedDate.getTime() !== createdDate.getTime()) {
        recentActivity.push({
          id: `project-updated-${project.id}`,
          type: "project",
          action: "updated",
          title: project.projectName,
          status: project.status,
          priority: project.priority,
          timestamp: updatedDate,
        });
      }
    });

    // Add task activities
    tasks.forEach(task => {
      const project = projects.find(p => p.id === task.projectId);
      const createdDate = new Date(task.createdAt);
      const updatedDate = new Date(task.updatedAt);
      
      recentActivity.push({
        id: `task-created-${task.id}`,
        type: "task",
        action: "created",
        title: task.taskTitle,
        status: task.status,
        priority: task.priority,
        timestamp: createdDate,
        projectName: project?.projectName,
      });

      // Only add update activity if it's different from creation
      if (updatedDate.getTime() !== createdDate.getTime()) {
        recentActivity.push({
          id: `task-updated-${task.id}`,
          type: "task",
          action: "updated",
          title: task.taskTitle,
          status: task.status,
          priority: task.priority,
          timestamp: updatedDate,
          projectName: project?.projectName,
        });
      }
    });

    // Sort by timestamp (most recent first) and take top 10
    recentActivity.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  const displayActivity = recentActivity.slice(0, 10);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 dark:text-green-400";
      case "active":
      case "in_progress":
        return "text-blue-600 dark:text-blue-400";
      case "blocked":
        return "text-red-600 dark:text-red-400";
      case "on_hold":
        return "text-yellow-600 dark:text-yellow-400";
      case "review":
        return "text-purple-600 dark:text-purple-400";
      default:
        return "text-muted-foreground";
    }
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "destructive" | "secondary" => {
    switch (priority) {
      case "critical":
      case "high":
        return "destructive";
      case "medium":
        return "default";
      case "low":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Project Manager Dashboard"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Project Manager" },
          { label: "Dashboard" }
        ]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="stats-grid">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
                <Folder className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold" data-testid="stat-active-projects">{stats.activeProjects}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
                <CheckSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold" data-testid="stat-total-tasks">{stats.totalTasks}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Blocked Tasks</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold" data-testid="stat-blocked-tasks">{stats.blockedTasks}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold" data-testid="stat-completion-rate">{completionRate}%</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Track your latest project and task updates</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-start gap-4">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayActivity.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Circle className="h-12 w-12 text-muted-foreground/20 mb-3" />
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                  <p className="text-xs text-muted-foreground mt-1">Create projects and tasks to see activity here</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="recent-activity-list">
                  {displayActivity.map((activity) => (
                    <div 
                      key={activity.id} 
                      className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0"
                      data-testid={`activity-item-${activity.type}-${activity.action}`}
                    >
                      <div className={`p-2 rounded-full ${activity.type === "project" ? "bg-blue-100 dark:bg-blue-950" : "bg-green-100 dark:bg-green-950"}`}>
                        {activity.action === "created" ? (
                          activity.type === "project" ? (
                            <Plus className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <Plus className="h-4 w-4 text-green-600 dark:text-green-400" />
                          )
                        ) : (
                          activity.type === "project" ? (
                            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <Edit className="h-4 w-4 text-green-600 dark:text-green-400" />
                          )
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" data-testid={`activity-title`}>
                              {activity.type === "project" ? (
                                <span className="text-blue-600 dark:text-blue-400">Project</span>
                              ) : (
                                <span className="text-green-600 dark:text-green-400">Task</span>
                              )}{" "}
                              {activity.action === "created" ? "created" : "updated"}: {activity.title}
                            </p>
                            {activity.projectName && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                in {activity.projectName}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {activity.priority && (
                              <Badge 
                                variant={getPriorityBadgeVariant(activity.priority)} 
                                className="text-xs"
                                data-testid={`activity-priority-badge`}
                              >
                                {activity.priority}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {activity.status && (
                            <span className={`text-xs font-medium ${getStatusColor(activity.status)}`} data-testid={`activity-status`}>
                              {activity.status.replace(/_/g, " ")}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground" data-testid={`activity-time`}>
                            {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
